import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, Plus, Sparkles, Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { SectionPageEditor } from '@/components/admin/SectionPageEditor';
import type { SectionDef } from '@/components/admin/SectionPageEditor';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

// pages/content/page.tsx (the "Skincare Intelligence" article hub) had
// zero i18n wiring before this — every content.* key existed in the
// locale bundles and even had rows pre-filled in rxk_site_content, but
// the component's actual text was hardcoded, so editing them here never
// did anything. Fixed by wiring the 3 hero fields + the article detail
// page's "All Articles" back-link to real t() calls. content.hero.subtitle's
// stale DB override (leftover draft text) was also corrected to match
// what's actually been live. The remaining content.* keys (categories,
// author, published, readTime, relatedArticles, backToHub) have no
// corresponding UI anywhere — left unwired rather than force-fitted.
// Category filter tabs and the article grid itself are dynamic
// (rxk_articles, below) or fixed in code, not admin text. Lives here
// (not its own sidebar page) since it's just the page text wrapped
// around this same article list.
const CONTENT_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Hub Page Text',
    icon: Sparkles,
    note: 'The category filter tabs and article grid below are the real content — this is just the hub page\'s own heading.',
    texts: [
      { key: 'content.hero.label', label: 'Eyebrow Label' },
      { key: 'content.hero.heading', label: 'Heading' },
      { key: 'content.hero.subtitle', label: 'Subtitle' },
    ],
  },
  {
    key: 'articleDetail',
    title: 'Article Detail Page',
    icon: Newspaper,
    texts: [{ key: 'content.allArticles', label: 'Back Link Text' }],
  },
  {
    key: 'seo',
    title: 'SEO',
    icon: Search,
    note: 'Shown in Google search results and social media link previews — not visible on the page itself. Individual article meta comes from the article\'s own title/excerpt/image, not from here.',
    texts: [
      { key: 'seo.content.title', label: 'Meta Title', long: true },
      { key: 'seo.content.description', label: 'Meta Description', long: true },
    ],
  },
];

interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  category: string;
  status: string;
  updated_at: string;
}

export default function AdminArticles() {
  const navigate = useNavigate();
  const { push } = useToast();
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('rxk_articles').select('id, slug, title, category, status, updated_at').order('updated_at', { ascending: false });
    setArticles((data as ArticleRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createArticle = async () => {
    setCreating(true);
    try {
      const slug = `new-article-${Date.now().toString().slice(-6)}`;
      const { data, error } = await supabase
        .from('rxk_articles')
        .insert({ title: 'New Article', slug, category: 'Skincare Tips', status: 'draft', body: [] })
        .select('id')
        .single();
      if (error) throw error;
      navigate(`/admin/articles/${data.id}`);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to create article');
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Content — Articles</h1>
          <Button size="sm" onClick={createArticle} disabled={creating}>
            <Plus className="w-3.5 h-3.5" /> New Article
          </Button>
        </div>

        <SectionPageEditor pageKey="content" title="Skincare Intelligence Hub — Page Text" subtitle="The heading/subtitle around the article list below, and the article detail page's back-link text" sections={CONTENT_SECTIONS} />

        <Card>
          <CardHeader title="Articles" subtitle={`${articles.length} total`} icon={<Newspaper className="w-4 h-4" />} />
          <CardBody className="space-y-1">
            {loading ? (
              <Spinner />
            ) : articles.length === 0 ? (
              <EmptyState title="No articles yet" />
            ) : (
              articles.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/admin/articles/${a.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink truncate">{a.title}</p>
                    <p className="text-[11px] text-ink-faint truncate">{a.category} · /{a.slug}</p>
                  </div>
                  <Badge tone={a.status === 'published' ? 'success' : 'neutral'}>{a.status}</Badge>
                </button>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
